import {
  Box,
  Checkbox,
  Group,
  NumberInput,
  SegmentedControl,
  SimpleGrid,
  Text,
  TextInput,
} from "@mantine/core";
import { SectionCard } from "../HomeBlockShared";
import { ACCENT, DESIGN_IMAGE, HomeBlock } from "../homeBlockData";
import { VideoConfig } from "../homeBlockConfig";
import classes from "../homeBlock.module.css";
import {
  Crop,
  EditableList,
  EditorLayout,
  fieldSetter,
  newId,
  ThumbPicker,
  useBlockConfig,
} from "./EditorKit";

const IMG = DESIGN_IMAGE.video;

const CARD_X = [33, 238, 443, 647, 851, 1055];

const SAMPLE_TITLES = [
  "Trên tay SSD Crucial P3: Hiệu năng vượt trội",
  "Trên tay RTX 5080: Hiệu năng AI cực mạnh!",
  "Màn hình gaming 2K 240Hz quá mượt!",
  "Hướng dẫn build PC từng bước cho người mới",
  "Review camera EZVIZ H6c 2K+: Nhỏ gọn, dễ dùng",
  "Trên tay MacBook Air M4: Ưu điểm mới, hiệu năng ấn tượng",
];

// Ảnh bìa mẫu cắt từ thiết kế: chỉ để xem khi chưa có ảnh, không lưu
const SAMPLE_CROPS = new Map<string, Crop>(
  SAMPLE_TITLES.map((_, i): [string, Crop] => [`video-${i + 1}`, { x: CARD_X[i], y: 82, w: 196, h: 150 }])
);

const initialConfig = (block: HomeBlock): VideoConfig => {
  const sample = block.code === "home-video";
  return {
    heading: {
      title: sample ? "VIDEO REVIEW" : block.name,
      highlight: sample ? "SẢN PHẨM MỚI NHẤT" : "",
      allLabel: sample ? "Xem tất cả video" : "",
      allLink: sample ? "/video" : "",
    },
    source: sample ? "list" : "playlist",
    playlist: { url: "", count: 6 },
    videos: sample
      ? SAMPLE_TITLES.map((title, i) => ({
          id: `video-${i + 1}`,
          title,
          youtubeUrl: "",
          coverImage: null,
        }))
      : [],
    show: ["views", "duration", "channel", "date"],
  };
};

const EditVideo = ({ block }: { block: HomeBlock }) => {
  const [config, setConfig] = useBlockConfig(block, () => initialConfig(block));
  const { heading, playlist, videos } = config;

  const patchHeading = (v: Partial<VideoConfig["heading"]>) =>
    setConfig((c) => ({ ...c, heading: { ...c.heading, ...v } }));
  const patchPlaylist = (v: Partial<VideoConfig["playlist"]>) =>
    setConfig((c) => ({ ...c, playlist: { ...c.playlist, ...v } }));
  const missingUrl = videos.filter((v) => !v.youtubeUrl).length;

  return (
    <EditorLayout block={block}>
      <SectionCard title="Đầu block">
        <SimpleGrid cols={{ base: 1, sm: 2 }} spacing={12}>
          <TextInput
            label="Tiêu đề"
            value={heading.title}
            onChange={(e) => patchHeading({ title: e.currentTarget.value })}
          />
          <TextInput
            label="Phần tô màu trong tiêu đề"
            value={heading.highlight}
            onChange={(e) => patchHeading({ highlight: e.currentTarget.value })}
          />
          <TextInput
            label="Nhãn Xem tất cả"
            value={heading.allLabel}
            onChange={(e) => patchHeading({ allLabel: e.currentTarget.value })}
          />
          <TextInput
            label="Link"
            value={heading.allLink}
            onChange={(e) => patchHeading({ allLink: e.currentTarget.value })}
          />
        </SimpleGrid>
      </SectionCard>

      <SectionCard title="Nguồn video">
        <Box>
          <Text fz={14} fw={500} mb={6} id="hb-video-source">
            Lấy video từ
          </Text>
          <SegmentedControl
            aria-labelledby="hb-video-source"
            value={config.source}
            onChange={(value) =>
              setConfig((c) => ({ ...c, source: value as VideoConfig["source"] }))
            }
            data={[
              { value: "playlist", label: "Playlist YouTube" },
              { value: "list", label: "Danh sách link" },
            ]}
          />
        </Box>
        {config.source === "playlist" ? (
          <SimpleGrid cols={{ base: 1, sm: 2 }} spacing={12}>
            <TextInput
              label="Link playlist"
              placeholder="https://www.youtube.com/playlist?list=..."
              value={playlist.url}
              onChange={(e) => patchPlaylist({ url: e.currentTarget.value })}
            />
            <NumberInput
              label="Số video hiển thị"
              min={3}
              max={12}
              value={playlist.count}
              onChange={(value) => typeof value === "number" && patchPlaylist({ count: value })}
            />
          </SimpleGrid>
        ) : (
          <>
            <EditableList
              items={videos}
              onChange={fieldSetter(setConfig, "videos")}
              itemLabel={(v) => v.title}
              columns={[
                { label: "Ảnh bìa", width: "72px" },
                { label: "Tiêu đề", width: "minmax(0, 1.2fr)" },
                { label: "Link YouTube", width: "minmax(0, 1fr)" },
              ]}
              renderCells={(v, update) => (
                <>
                  <ThumbPicker
                    label={v.title}
                    src={v.coverImage}
                    imageId={IMG}
                    crop={SAMPLE_CROPS.get(v.id)}
                    onPick={(url) => update({ coverImage: url })}
                    width={72}
                  />
                  <TextInput
                    aria-label="Tiêu đề video"
                    value={v.title}
                    onChange={(e) => update({ title: e.currentTarget.value })}
                  />
                  <TextInput
                    aria-label={`Link YouTube ${v.title}`}
                    placeholder="https://www.youtube.com/watch?v=..."
                    value={v.youtubeUrl}
                    onChange={(e) => update({ youtubeUrl: e.currentTarget.value })}
                  />
                </>
              )}
              newItem={() => ({ id: newId(), title: "", youtubeUrl: "", coverImage: null })}
              addLabel="Thêm video"
              max={12}
              minWidth={600}
            />
            {missingUrl > 0 && (
              <Text fz={12} c="orange.8">
                {missingUrl} video chưa có link YouTube. Không tải ảnh bìa thì web lấy ảnh bìa
                của YouTube; lượt xem và thời lượng tự lấy sau khi dán link.
              </Text>
            )}
          </>
        )}
      </SectionCard>

      <SectionCard title="Hiển thị trên thẻ video">
        <Checkbox.Group
          value={config.show}
          onChange={(value) => setConfig((c) => ({ ...c, show: value as VideoConfig["show"] }))}
        >
          <Group gap={20}>
            <Checkbox color={ACCENT} value="views" label="Lượt xem" />
            <Checkbox color={ACCENT} value="duration" label="Thời lượng" />
            <Checkbox color={ACCENT} value="channel" label="Tên kênh" />
            <Checkbox color={ACCENT} value="date" label="Ngày đăng" />
          </Group>
        </Checkbox.Group>
        <Text fz={12} className={classes.muted}>
          Số liệu lấy trực tiếp từ YouTube, không nhập tay.
        </Text>
      </SectionCard>
    </EditorLayout>
  );
};

export default EditVideo;
